import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const VDIsAndSafetyChecks = () => (
  <Page
    title="VDIs and Safety Checks"
    intro="Vehicle Daily Inspections (VDIs) are the crew's same-day roadworthiness check; vehicle safety checks are a separate, more formal periodic inspection. Both are recorded and reviewed in AmbuNet."
  >
    <Section title="Recording a VDI">
      <Step number={1} title="Open the form">
        Go to <DocLink url="/vdi" text="Record VDI" /> and pick the vehicle.
        The date/time defaults to now.
      </Step>
      <Step number={2} title="Record the basics">
        Enter the fuel level and mileage, and add any notes.
      </Step>
      <Step number={3} title="Work through the checklist">
        Answer the inspection checklist for that vehicle type — the
        questions are configured per vehicle type by your Make Ready team,
        so an ambulance and a response car get different lists.
      </Step>
      <Step number={4} title="Drug bags and attachments">
        Confirm each drug bag aboard — whether it is tagged and whether the
        seal number matches the record. The bag picker lists the bags
        recorded on this vehicle first, then <strong>Other bags</strong> with
        where each one currently is; choosing one of those shows{' '}
        <strong>Will be assigned to this vehicle</strong>, so the bag's
        record moves with it when the VDI is submitted. If the seal carries
        a QR or data-matrix code you can scan it instead of reading the
        number by eye, which marks the check as verified by scan. A bag
        found without its recorded seal is reported as seal-removed when the
        check is submitted. Attach photos or paperwork if needed.
      </Step>
      <Step number={5} title="Flag problems">
        Answer whether the VDI contains defects that need reporting and
        whether the vehicle is safe to continue using. Flagging defects
        takes you into raising a defect straight from the inspection.
      </Step>
      <Info title="Who can do this?">
        Any signed-in crew member can record a VDI — it is deliberately not
        restricted by team. The checklist questions are configured from{' '}
        <DocLink
          url="/site-configuration/vdi"
          text="Configuration > Fleet > VDI"
        />
        .
      </Info>
    </Section>

    <Section title="Reviewing completed VDIs">
      <Bullets>
        <Bullet>
          Open <DocLink url="/vdis" text="Completed VDIs" /> to review
          submitted inspections, filtered by vehicle, employee, and date
          range. Click one to see the full report.
        </Bullet>
        <Bullet>
          The sidebar shows the VDIs link to the Fleet, Manager, and
          Supervisor teams (and site administrators).
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Safety checks">
      <p>
        Vehicle safety checks are recorded from{' '}
        <DocLink url="/fleet/record-safety-check" text="Record Safety Check" />
        . A safety check records the vehicle, mileage, and date, who carried
        it out — an internal employee or an external garage — and answers to
        the safety check question set. Completed checks are listed on the
        vehicle record, with a full report view for each.
      </p>
      <Info title="Amending and deleting a check">
        On a completed safety check report, members of the{' '}
        <strong>Fleet</strong> team (and site administrators) can{' '}
        <strong>Amend</strong> the recorded answers and details or{' '}
        <strong>Delete</strong> the check entirely. An amended report is
        stamped with who amended it and when, so the record stays honest.
      </Info>
      <Info title="Not the same form">
        VDIs are a quick same-day crew inspection; safety checks are the
        formal periodic record. The safety check questions can be customised
        from Configuration, and the safety check, deep clean, and servicing
        frequencies set there drive the due/overdue highlighting on the
        vehicle list.
      </Info>
      <Info title="Start of Shift combines the checks">
        Crew starting a shift can use{' '}
        <DocLink url="/start-of-shift" text="Start of Shift" /> to complete
        the VDI and the vehicle's Make Ready equipment check in one flow —
        see{' '}
        <DocLink
          url="/fleet-equipment/make-ready-and-maintenance"
          text="Make Ready and Maintenance"
          docsPage
        />
        .
      </Info>
    </Section>
  </Page>
)

export default VDIsAndSafetyChecks
